import { useCallback, useRef, useState } from "react";

const TOAST_EXIT_MS = 260;

export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const closeToast = useCallback(
    (id) => {
      delete timersRef.current[id];
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === id ? { ...toast, closing: true } : toast
        )
      );
      setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== id));
      }, TOAST_EXIT_MS);
    },
    []
  );

  const startTimer = useCallback(
    (id, delay) => {
      const timerId = setTimeout(() => closeToast(id), delay);
      timersRef.current[id] = { timerId, startedAt: Date.now(), remaining: delay };
    },
    [closeToast]
  );

  const pauseToast = useCallback((id) => {
    const entry = timersRef.current[id];
    if (!entry) return;
    clearTimeout(entry.timerId);
    const elapsed = Date.now() - entry.startedAt;
    entry.remaining = Math.max(entry.remaining - elapsed, 0);
    entry.timerId = null;
  }, []);

  const resumeToast = useCallback(
    (id) => {
      const entry = timersRef.current[id];
      if (!entry || entry.timerId != null) return;
      startTimer(id, entry.remaining);
    },
    [startTimer]
  );

  const notify = useCallback(
    (payload) => {
      // De-dupe by stable key: callers can pass `key` so the same logical
      // toast (e.g. "license expired") doesn't stack on every page mount.
      // First call wins; later calls with the same key return the
      // existing id so callers tracking it stay in sync.
      const key = payload?.key;
      if (key) {
        let existingId = null;
        setToasts((prev) => {
          existingId = prev.find((t) => t.key === key)?.id || null;
          return prev;
        });
        if (existingId) {
          payload?.onCreated?.(existingId);
          return existingId;
        }
      }
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const toast = {
        id,
        key,
        type: payload?.type || "info",
        title: payload?.title || "Update",
        message: payload?.message || "",
        // Optional inline action: { label, onClick } renders as a link/button
        // in the toast body. Used by the license toast for "Renew now".
        action: payload?.action || null,
        closing: false,
      };
      setToasts((prev) => [...prev, toast]);
      // duration:0 = persistent (no auto-dismiss). User can still close
      // via the X button. Used for license / system-state notices that
      // should stay visible until the user acknowledges them.
      const ttl = payload?.duration ?? 3500;
      if (ttl > 0) {
        const delay = Math.max(ttl - TOAST_EXIT_MS, 0);
        startTimer(id, delay);
      }
      // Hand the freshly-minted id to the caller. Used by hooks that
      // want to dismiss programmatically when their underlying state
      // changes (e.g. license recovers → useLicenseToast clears the
      // toast it fired).
      payload?.onCreated?.(id);
      return id;
    },
    [startTimer]
  );

  const dismissToast = useCallback(
    (id) => {
      const entry = timersRef.current[id];
      if (entry?.timerId) clearTimeout(entry.timerId);
      closeToast(id);
    },
    [closeToast]
  );

  return { toasts, notify, dismissToast, pauseToast, resumeToast };
}
